import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';

export interface TimeEntry {
  id: string;
  taskId: string;
  userId: string;
  date: string; // YYYY-MM-DD
  duration: number; // milliseconds
  note: string | null;
  createdAt: string;
}

interface SupabaseTimeEntryRow {
  id: string;
  task_id: string;
  user_id: string;
  date: string;
  duration: number;
  note: string | null;
  created_at: string;
}

function convert(row: SupabaseTimeEntryRow): TimeEntry {
  return {
    id: row.id,
    taskId: row.task_id,
    userId: row.user_id,
    date: row.date,
    duration: row.duration,
    note: row.note,
    createdAt: row.created_at,
  };
}

/** Fetch time entries for a date range, optionally filtered by user */
export async function fetchTimeEntries(opts: {
  startDate: string; // YYYY-MM-DD
  endDate: string;
  userId?: string;
  taskId?: string;
}): Promise<TimeEntry[]> {
  let query = supabase
    .from('time_entries')
    .select('*')
    .gte('date', opts.startDate)
    .lte('date', opts.endDate);

  if (opts.userId) query = query.eq('user_id', opts.userId);
  if (opts.taskId) query = query.eq('task_id', opts.taskId);

  const { data, error } = await withRetry(() => query.order('date', { ascending: true }));
  if (error) throw error;
  return (data || []).map(convert);
}

/** Get total duration per task for a date range */
export async function fetchTimeByTask(opts: {
  startDate: string;
  endDate: string;
  userId?: string;
}): Promise<Record<string, number>> {
  const entries = await fetchTimeEntries(opts);
  const byTask: Record<string, number> = {};
  for (const e of entries) {
    byTask[e.taskId] = (byTask[e.taskId] || 0) + e.duration;
  }
  return byTask;
}

/** Upsert a time entry for a task+user+date (adds to existing or creates new) */
export async function addTimeEntry(opts: {
  taskId: string;
  userId: string;
  date: string;
  duration: number;
  note?: string;
}): Promise<TimeEntry> {
  // Check if entry already exists for this task+user+date
  const { data: existing } = await withRetry(() =>
    supabase
      .from('time_entries')
      .select('*')
      .eq('task_id', opts.taskId)
      .eq('user_id', opts.userId)
      .eq('date', opts.date)
      .maybeSingle()
  );

  if (existing) {
    // Add to existing duration
    const newDuration = existing.duration + opts.duration;
    const { data, error } = await withRetry(() =>
      supabase
        .from('time_entries')
        .update({ duration: newDuration, note: opts.note ?? existing.note })
        .eq('id', existing.id)
        .select()
        .single()
    );
    if (error || !data) throw error || new Error('Failed to update time entry');
    return convert(data);
  }

  // Create new entry
  const { data, error } = await withRetry(() =>
    supabase
      .from('time_entries')
      .insert({
        task_id: opts.taskId,
        user_id: opts.userId,
        date: opts.date,
        duration: opts.duration,
        note: opts.note || null,
      })
      .select()
      .single()
  );
  if (error || !data) throw error || new Error('Failed to create time entry');
  return convert(data);
}

/** Set exact duration for a task+user+date */
export async function setTimeEntry(opts: {
  taskId: string;
  userId: string;
  date: string;
  duration: number;
  note?: string;
}): Promise<TimeEntry> {
  const { data: existing } = await withRetry(() =>
    supabase
      .from('time_entries')
      .select('*')
      .eq('task_id', opts.taskId)
      .eq('user_id', opts.userId)
      .eq('date', opts.date)
      .maybeSingle()
  );

  if (existing) {
    const { data, error } = await withRetry(() =>
      supabase
        .from('time_entries')
        .update({ duration: opts.duration, note: opts.note ?? existing.note })
        .eq('id', existing.id)
        .select()
        .single()
    );
    if (error || !data) throw error || new Error('Failed to update time entry');
    return convert(data);
  }

  const { data, error } = await withRetry(() =>
    supabase
      .from('time_entries')
      .insert({
        task_id: opts.taskId,
        user_id: opts.userId,
        date: opts.date,
        duration: opts.duration,
        note: opts.note || null,
      })
      .select()
      .single()
  );
  if (error || !data) throw error || new Error('Failed to create time entry');
  return convert(data);
}

/** Delete a time entry */
export async function deleteTimeEntry(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.from('time_entries').delete().eq('id', id)
  );
  if (error) throw error;
}
